import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { useIsActiveTurn, useUser } from '../../../store'
import LoginPrompt from '../../auth/LoginPrompt'
import { ResizableDiv } from '../../elements/ResizableDiv'
import { ScrollToEnd } from '../ScrollToEnd'
import { ContextBarWrapper } from './context/context-bar-wrapper'
import { MentionsMenuPopover } from './mentions'
import { ChatInputButtons } from './nested/buttons'
import { FilePasteHandler } from './nested/file-paste-handler'
import { MessageInput } from './nested/message-input'
import { ScrollShortcutHandler } from './nested/scroll-shortcut-handler'
import { TabTypingAssistanceHandler } from './nested/tab-typing-assistance-handler'
import { WindowContextTabToggleHandler } from './nested/window-context-tab-toggle-handler'
import { useDictationPlaceholder } from './hooks/use-dictation-placeholder'
import { usePrepareTurnOnComposerUse } from './hooks/use-prepare-turn-on-composer-use'
import { ChatNotices } from './nested/chat-notices'

// The composer's public surface. Nothing here namespaces its slots onto
// `ChatInput`: every nested leaf is an internal of this card with no external
// consumer, so a compound API would be dead surface (`components.mdc` §5 —
// don't force the pattern onto feature UI). Import a leaf directly if one ever
// needs to stand alone.
export { ChatInputElementId } from './chat-input-element-id'
export { useExternalFocusChatInput, useFocusChatInput } from './hooks/use-focus-chat-input'

interface ChatInputProps {
  chatId?: string
  className?: string
  contextBarClassName?: string
  placeholder?: string
  disabled?: boolean
  hideScrollToEnd?: boolean
  onSubmit?: (conversationId: string) => void
  handleSubmit?: () => void
  /**
   * Mint the next send's Arkose token while the user types. Each mint is a
   * billed assessment, so turn it off where Enter may not send a chat turn.
   * Defaults to on.
   */
  prefetchesSendChallenge?: boolean
  isLoading?: boolean
  /**
   * Suppresses the document-level type-to-focus listener. That listener
   * already ignores keys while any input/textarea/contenteditable is focused,
   * so this only matters for the window where an open find bar's input has
   * been blurred to nowhere by Chromium's find internals — without it, the
   * next keystroke would land here and win the focus fight before the find
   * bar's resync can reclaim it (see sidepanel-find-in-page.tsx).
   */
  typeToFocusDisabled?: boolean
  /**
   * Rendered inside the input's card, above the context bar — for surfaces that
   * belong to the composer rather than floating over it (the sidepanel's
   * in-flight chats). Clicks inside it don't focus the input.
   */
  header?: ReactNode
  /**
   * Rendered above the input card (not inside it) — for chrome that precedes
   * the composer rather than living in it, like the meeting view's audio
   * permissions card. Clicks inside it don't focus the input.
   */
  before?: ReactNode
  /** Positions the notices stack above the input, e.g. to inset it when the card spans the full width. */
  noticesClassName?: string
}

export const ChatInput = memo(
  ({
    className,
    contextBarClassName,
    placeholder,
    disabled,
    chatId,
    hideScrollToEnd,
    onSubmit,
    handleSubmit,
    prefetchesSendChallenge,
    isLoading,
    typeToFocusDisabled,
    header,
    before,
    noticesClassName,
  }: ChatInputProps) => {
    const isChatLoading = useIsActiveTurn()
    const inputRef = useRef<HTMLElement>(null)
    const user = useUser()
    const [wasFocused, setWasFocused] = useState(false)
    const dictationReadyPlaceholder = useDictationPlaceholder(placeholder)
    // A custom `handleSubmit` sends somewhere other than a chat turn to `chatId`, such as a staged-action edit.
    const prepareTurnHandlers = usePrepareTurnOnComposerUse({ chatId, enabled: !handleSubmit && !disabled })

    const onClickInput = useCallback(() => {
      inputRef.current?.focus()
    }, [])

    const showLoginPrompt = (!user || user.isAnonymous || user.provider === 'supabase') && !isChatLoading

    // A turn finishing re-focuses the composer, but only if focus was handed to
    // it programmatically moments ago — otherwise it would steal focus from
    // wherever the user went while the answer streamed.
    useEffect(() => {
      if (!isChatLoading && wasFocused) {
        inputRef.current?.focus()
      }
    }, [isChatLoading])

    useEffect(() => {
      const el = inputRef.current
      let timeout: ReturnType<typeof setTimeout> | undefined
      const onFocus = () => {
        setWasFocused(true)
        timeout = setTimeout(() => {
          setWasFocused(false)
          timeout = undefined
        }, 200)
      }
      if (el) {
        el.addEventListener('external-focus', onFocus)
      }
      return () => {
        el?.removeEventListener('external-focus', onFocus)
        if (timeout) {
          clearTimeout(timeout)
          timeout = undefined
        }
      }
    }, [showLoginPrompt])

    if (showLoginPrompt) {
      return <LoginPrompt />
    }

    return (
      <>
        <FilePasteHandler />
        <WindowContextTabToggleHandler />
        <TabTypingAssistanceHandler />
        <ScrollShortcutHandler />
        {/* stopPropagation: the card focuses the input on click, and `before`
            content holds its own controls (e.g. permission toggles). */}
        <div className="w-full cursor-default" onClick={(e) => e.stopPropagation()}>
          <ChatNotices className={noticesClassName} />
          {before}
        </div>
        <div
          className={twMerge(
            'isolate transition-[color,min-height] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]',
            'text-foreground relative flex min-h-[60px] w-full flex-1 cursor-text flex-col items-center text-sm font-normal',

            // Fully opaque: the input floats over view content (and the state
            // glow), so any translucency lets it bleed through.
            'bg-background ring-border rounded-2xl ring-1 ring-inset focus-within:ring-[color:var(--focus-ring-color)] [&:hover:not(:focus-within)]:ring-[color:var(--border-hover)]',
            // Lives on the card itself, not the outer motion.div: with a `before`
            // slot rendered above the card the outer element spans both, and the
            // shadow drew a rectangle behind the gap between them instead of
            // hugging the card.
            'shadow-[0px_0px_4px_2px_rgba(0,0,0,0.05),0px_0px_8px_8px_rgba(0,0,0,0.05)]',
            className,
          )}
          onClick={onClickInput}
        >
          {/* stopPropagation: the card focuses the input on click, and the
              header holds its own controls (rows that open a chat). */}
          {header && (
            <div className="w-full cursor-default" onClick={(e) => e.stopPropagation()}>
              {header}
            </div>
          )}
          <ResizableDiv className={'!w-full'} containerClassName={'w-full p-0'}>
            <ContextBarWrapper className={contextBarClassName} />
          </ResizableDiv>
          <MentionsMenuPopover alignOffset={10}>
            <div
              className={
                // gap-y-2 only bites once the text wraps the controls onto their
                // own line, where they would otherwise sit against the last line.
                'flex h-fit min-h-14 w-full cursor-text flex-wrap items-end justify-between gap-y-2 px-3.5 py-3.5 pl-2.5 transition-[padding,min-height] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
              }
              {...prepareTurnHandlers}
            >
              <MessageInput
                ref={inputRef}
                chatId={chatId}
                placeholder={dictationReadyPlaceholder}
                disabled={disabled}
                onSubmit={onSubmit}
                handleSubmit={handleSubmit}
                prefetchesSendChallenge={prefetchesSendChallenge}
                isLoading={isLoading}
                typeToFocusDisabled={typeToFocusDisabled}
              />
              <ChatInputButtons chatId={chatId} handleSubmit={handleSubmit} isLoading={isLoading} onSubmit={onSubmit} />
            </div>
          </MentionsMenuPopover>
          {!hideScrollToEnd && <ScrollToEnd />}
        </div>
      </>
    )
  },
)
ChatInput.displayName = 'ChatInput'
